/**
 * Magic Link Authentication Page
 *
 * Handles magic link token validation and user authentication.
 * Validates the token from the URL, exchanges it for a session token,
 * and redirects users to the appropriate page based on their role.
 */

import React, { useState, useEffect } from "react";
import { MAGIC_LINK_AUTH_TYPE_KEY } from "../util/authCleanup";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import styled from "styled-components";

// GraphQL mutation for magic link token exchange
const EXCHANGE_MAGIC_LINK_TOKEN = gql`
  mutation ExchangeMagicLinkToken($token: String!) {
    authExchangeMagicLinkToken(token: $token) {
      sessionToken
      user {
        id
        email
        name
        role
      }
      success
    }
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const Card = styled.div`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 20px;
  font-size: 24px;
`;

const Message = styled.p`
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const InstructionText = styled.p`
  color: #555;
  margin-bottom: 20px;
  line-height: 1.6;
  font-size: 14px;
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const LoadingSpinner = styled.div`
  border: 2px solid #f3f3f3;
  border-top: 2px solid #1976d2;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Button = styled.button`
  background-color: #1976d2;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1565c0;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

/**
 * Magic Link Authentication Page Component
 */
const MagicLinkAuthPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  const [exchangeToken] = useMutation(EXCHANGE_MAGIC_LINK_TOKEN, {
    onCompleted: (data) => {
      const result = data?.authExchangeMagicLinkToken;

      if (result?.success && result.sessionToken) {
        console.log(
          "[MagicLinkAuth] Authentication successful - storing tokens",
          {
            userId: result.user.id,
            role: result.user.role,
            tokenLength: result.sessionToken.length,
            timestamp: new Date().toISOString(),
          },
        );

        // Store session token in both localStorage AND sessionStorage for redundancy
        // This helps diagnose if localStorage is being cleared/blocked
        try {
          localStorage.setItem("magicLinkSessionToken", result.sessionToken);
          // Persist auth-type flag so the session-expired modal still shows
          // "Request New Link" after the session token is cleared on expiry.
          localStorage.setItem(MAGIC_LINK_AUTH_TYPE_KEY, "magic_link");
          console.log("[MagicLinkAuth] ✓ Token stored in localStorage");
        } catch (localStorageError) {
          console.error(
            "[MagicLinkAuth] ✗ Failed to store token in localStorage:",
            localStorageError,
          );
        }

        try {
          sessionStorage.setItem("magicLinkSessionToken", result.sessionToken);
          console.log(
            "[MagicLinkAuth] ✓ Token stored in sessionStorage (backup)",
          );
        } catch (sessionStorageError) {
          console.error(
            "[MagicLinkAuth] ✗ Failed to store token in sessionStorage:",
            sessionStorageError,
          );
        }

        // Store user data for the application
        const userData = result.user;
        localStorage.setItem("userId", userData.id);
        sessionStorage.setItem("userId", userData.id);

        // Verify storage was successful before reloading
        const verifyLocal = localStorage.getItem("magicLinkSessionToken");
        const verifySession = sessionStorage.getItem("magicLinkSessionToken");

        console.log("[MagicLinkAuth] Storage verification:", {
          localStorageHasToken: !!verifyLocal,
          sessionStorageHasToken: !!verifySession,
          localStorageMatches: verifyLocal === result.sessionToken,
          sessionStorageMatches: verifySession === result.sessionToken,
          cookiesEnabled: navigator.cookieEnabled,
          userAgent: navigator.userAgent,
        });

        if (!verifyLocal && !verifySession) {
          console.error(
            "[MagicLinkAuth] CRITICAL: Both storage methods failed! Token will be lost on reload.",
          );
          setError(
            "Your browser settings may be blocking authentication. Please enable cookies and local storage, then try again.",
          );
          setIsProcessing(false);
          return;
        }

        if (!verifyLocal) {
          console.warn(
            "[MagicLinkAuth] localStorage verification failed, but sessionStorage succeeded. Proceeding with reload.",
          );
        }

        console.log(
          "[MagicLinkAuth] Reloading page to initialize authenticated session",
        );
        // Ensure Apollo picks up the Authorization header by reloading the page
        // and avoid any CF/Okta session precedence. Hard reload to root; router will send to /files or /admin.
        window.location.replace("/");
      } else {
        setError("Authentication failed. Please request a new magic link.");
        setIsProcessing(false);
      }
    },
    onError: (error) => {
      console.error("[MagicLinkAuth] Authentication error:", error);

      // Provide user-friendly error messages based on GraphQL error
      let errorMessage =
        "Authentication failed. Please request a new magic link.";

      if (error.message.includes("expired")) {
        errorMessage = "This magic link has expired. Please request a new one.";
      } else if (error.message.includes("already used")) {
        errorMessage =
          "This magic link has already been used. Please request a new one.";
      } else if (error.message.includes("invalid")) {
        errorMessage = "This magic link is invalid. Please request a new one.";
      }

      setError(errorMessage);
      setIsProcessing(false);
    },
  });

  useEffect(() => {
    // Validate token parameter and initiate authentication
    if (!token) {
      setError("No authentication token provided.");
      setIsProcessing(false);
      return;
    }

    if (typeof token !== "string" || token.length < 10) {
      setError("Invalid authentication token format.");
      setIsProcessing(false);
      return;
    }

    console.log("[MagicLinkAuth] Processing magic link token");

    // Exchange token for session
    exchangeToken({
      variables: { token },
    });
  }, [token, exchangeToken]);

  const handleRequestNewLink = () => {
    console.log("[MagicLinkAuth] Redirecting to request new link");
    navigate("/auth/request", { replace: true });
  };

  if (isProcessing) {
    return (
      <Container>
        <Card>
          <Title>Signing you in...</Title>
          <LoadingSpinner />
          <Message>Please wait while we verify your magic link.</Message>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card>
          <Title>Authentication Error</Title>
          <ErrorMessage>{error}</ErrorMessage>
          <InstructionText>
            To get a new sign-in link, click the button below and enter your
            email address. A new link will be sent to your inbox.
          </InstructionText>
          <Button onClick={handleRequestNewLink}>Request New Link</Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Authentication Successful</Title>
        <Message>Redirecting you to the application...</Message>
      </Card>
    </Container>
  );
};

export default MagicLinkAuthPage;
